// moment-timezone re-exports the moment API plus `.tz()`. Importing it here (not
// plain 'moment') guarantees `.tz()` is available in this file instead of relying
// on another module having loaded the timezone side-effect first.
import moment from 'moment-timezone';

export const currentTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * The IANA timezone to attribute this user's activity to — the single
 * definition used whenever we send a timezone to an API.
 *
 * Resolution order matches what the rest of the app already does
 * (`localStorage.timezone_cache || currentTZ`, see the navbar clock and
 * attendance views): a geolocation-derived zone cached by `getSmartTimezone()`
 * wins over the OS setting, since a user travelling with a laptop still on home
 * time should see their real local hours.
 *
 * Always returns something — falls back to 'UTC' rather than throwing, so a
 * locked-down browser can never break a dashboard request.
 *
 * @returns {string} e.g. 'Asia/Dubai'
 */
export const getUserTimezone = () => {
	try {
		const cached = localStorage.getItem('timezone_cache');
		if (cached) return cached;
		return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
	} catch (e) {
		// Private mode / disabled storage / no Intl data.
		return 'UTC';
	}
};

export const formatLastMessageTime = (timestamp) => {
	if (!timestamp) return '';

	// Fallback to browser timezone if none provided
	const tz = localStorage.getItem('timezone_cache') || currentTZ;

	// Create moment object with proper timezone
	const m = moment(timestamp).tz(tz);

	// Within last minute → "Now"
	if (moment().tz(tz).diff(m) < 60 * 1000) {
		return 'Now';
	}

	// Today → Show time (e.g., "2:30 PM")
	if (m.isSame(moment().tz(tz), 'day')) {
		return m.format('h:mm A');
	}

	// Yesterday → "Yesterday"
	if (m.isSame(moment().tz(tz).subtract(1, 'day'), 'day')) {
		return 'Yesterday';
	}

	// This year → Show day/month (e.g., "May 27")
	if (m.isSame(moment().tz(tz), 'year')) {
		return m.format('MMM D');
	}

	// Older than current year → Full date (e.g., "27/05/2023")
	return m.format('DD/MM/YYYY');
};

export const formattedDate = (_date) => {
	if (_date === '') {
		return '';
	}

	const date = new Date(_date);

	// Get the formatted date
	const options = {
		weekday: 'short',
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	};
	const formattedDate = date.toLocaleDateString('en-US', options);

	return formattedDate;
};

export const capitalizeWords = (str = '') =>
	str
		.toLowerCase()
		.split(' ')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');

// export const formatCurrency = (amount, currency) => {
// 	return new Intl.NumberFormat('en-AE', {
// 		style: 'currency',
// 		currency: currency || 'AED',
// 		minimumFractionDigits: 0,
// 		maximumFractionDigits: 2,
// 	}).format(amount);
// };

export const formatCurrency = (amount, currency = 'AED') => {
	// Ensure the amount is a valid number
	const value = Number(amount);
	if (Number.isNaN(value)) return formatCurrency(0, currency); // fallback to 0

	// Validate currency code (basic ISO 4217 check: 3 letters)
	const safeCurrency =
		typeof currency === 'string' && currency.length === 3
			? currency.toUpperCase()
			: 'AED';

	try {
		return new Intl.NumberFormat('en-AE', {
			style: 'currency',
			currency: safeCurrency,
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
		}).format(value);
	} catch (err) {
		// Fallback in case Intl fails
		console.warn('Currency formatting failed:', err);
		return `${safeCurrency} ${value.toFixed(2)}`;
	}
};

export const formatAmount = (amount) => {
	return new Intl.NumberFormat('en-AE', {
		style: 'decimal',
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	}).format(amount);
};

export const toCapitalCase = (str) =>
	str
		.toLowerCase()
		.split(' ')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');

export const validatePhoneNumber = (phoneNumber) => {
	if (!phoneNumber) return null;

	// Remove all spaces
	let sanitized = phoneNumber.toString().replace(/\s+/g, '');

	// Remove leading '+' or '00'
	if (sanitized.startsWith('+')) {
		sanitized = sanitized.slice(1);
	} else if (sanitized.startsWith('00')) {
		sanitized = sanitized.slice(2);
	}

	// Validate: starts with non-zero digit and 10-15 digits long
	if (!/^[1-9]\d{9,14}$/.test(sanitized)) {
		console.log('invalid number', sanitized);
		return null;
	}

	return sanitized;
};

// export const validatePhoneNumber = (phoneNumber) => {
// 	const sanitized = phoneNumber.replace(/\s+/g, '').replace(/^(\+?)/, '');

// 	if (!/^[1-9]\d{9,14}$/.test(sanitized)) {
// 		console.log('invalid number', sanitized);
// 		return null;
// 	}

// 	return sanitized;
// };

// export const formatPostDate = (date, timezone) => {
// 	const now = new Date();
// 	let inputDate = new Date(date);

// 	console.log(inputDate);

// 	// Apply timezone offset if provided
// 	if (timezone) {
// 		const offset = inputDate.getTimezoneOffset() * 60000;
// 		const timezoneOffset = parseInt(timezone) * 3600000;
// 		console.log(offset, timezoneOffset);
// 		inputDate = new Date(inputDate.getTime() + offset + timezoneOffset);

// 		console.log(inputDate);
// 	}

// 	const seconds = Math.floor((now - inputDate) / 1000);

// 	// Less than 1 minute
// 	// if (seconds < 60) {
// 	// 	return seconds <= 0 ? 'now' : `${seconds} sec ago`;
// 	// }

// 	if (seconds < 60) {
// 		return 'now';
// 	}

// 	// Less than 1 hour
// 	const minutes = Math.floor(seconds / 60);
// 	if (minutes < 60) {
// 		return `${minutes} min ago`;
// 	}

// 	// Less than 24 hours
// 	const hours = Math.floor(minutes / 60);
// 	if (hours < 24) {
// 		return `${hours}h ago`;
// 	}

// 	// 24 hours or older - show full date with time
// 	const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// 	const weekday = weekdays[inputDate.getDay()];
// 	const day = inputDate.getDate().toString().padStart(2, '0');
// 	const month = [
// 		'Jan',
// 		'Feb',
// 		'Mar',
// 		'Apr',
// 		'May',
// 		'Jun',
// 		'Jul',
// 		'Aug',
// 		'Sep',
// 		'Oct',
// 		'Nov',
// 		'Dec',
// 	][inputDate.getMonth()];
// 	const year = inputDate.getFullYear().toString().slice(-2);

// 	// Format time as 09:24 PM
// 	let hours12 = inputDate.getHours();
// 	const ampm = hours12 >= 12 ? 'PM' : 'AM';
// 	hours12 = hours12 % 12;
// 	hours12 = hours12 ? hours12 : 12; // the hour '0' should be '12'
// 	const mins = inputDate.getMinutes().toString().padStart(2, '0');
// 	const timeString = `${hours12}:${mins} ${ampm}`;

// 	return `${weekday} ${day} ${month} ${year}, ${timeString}`;
// };

// `timezone` defaults to the current user's zone (geolocation-cached, else OS,
// else UTC) so callers get correct LOCAL wall-clock times for free. Note: only
// the absolute-date fallback is timezone-sensitive — the "X ago" relative diff is
// an absolute elapsed-time comparison and reads the same in every zone by design.
export const formatPostDate = (date, timezone = getUserTimezone()) => {
	try {
		const tz = timezone || 'UTC';

		// `.tz(tz)` only changes the DISPLAY zone, not the underlying instant, so
		// the diff below stays an absolute (timezone-independent) elapsed time.
		const now = moment().tz(tz);
		const inputDate = moment(date).tz(tz);

		if (!inputDate.isValid()) return 'Invalid date';

		// Clamp negatives: if a timestamp is slightly AHEAD of the viewer's clock
		// (clock skew), show "now" rather than a future/garbage value.
		const diffSeconds = Math.max(0, now.diff(inputDate, 'seconds'));

		// Relative time formats
		if (diffSeconds < 60) return 'now';
		if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)} min ago`;
		if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;

		// Older notes: absolute wall-clock time, rendered in the USER'S timezone.
		return inputDate.format('ddd DD MMM YY, hh:mm A');
	} catch (error) {
		console.error('Date formatting error:', error);
		return 'Invalid date';
	}
};

export const toUTCString = (date) => {
	return date
		? moment(date).utcOffset(0, true).startOf('day').toISOString()
		: null;
};

export const toPureUTCString = (date) => {
	return date
		? moment(date).utcOffset(0, true).startOf('day').format('YYYY-MM-DD')
		: null;
};

export const formatDNS = (dateStr) => {
	const date = new Date(dateStr);
	return date.toLocaleDateString('en-GB', {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
	});
};

// export const extractLocationData = (ipString, countryNames) => {
// 	const countryList = new Set(countryNames);

// 	if (ipString === null) {
// 		return { ip: null, country: null, city: null };
// 	}

// 	const parts = ipString?.split('-').map((part) => part.trim());

// 	let ip = null,
// 		city = null,
// 		country = null;

// 	const isValidIPv4 = (str) => {
// 		const octets = str.split('.');
// 		return (
// 			octets.length === 4 &&
// 			octets.every(
// 				(octet) =>
// 					/^\d+$/.test(octet) && Number(octet) >= 0 && Number(octet) <= 255
// 			)
// 		);
// 	};

// 	// If first part is a valid IPv4 address or a pure number, set it as IP
// 	if (isValidIPv4(parts[0]) || /^\d*$/.test(parts[0])) {
// 		ip = parts.shift(); // Remove IP so remaining parts are city & country
// 	}

// 	if (parts.length === 2) {
// 		const firstPartLower = parts[0].toLowerCase();
// 		const secondPartLower = parts[1].toLowerCase();

// 		if (countryList.has(secondPartLower)) {
// 			city = parts[0];
// 			country = parts[1];
// 		} else if (countryList.has(firstPartLower)) {
// 			country = parts[0];
// 			city = parts[1]; // Handle misplaced country
// 		} else {
// 			city = parts[0];
// 			country = null;
// 		}
// 	} else if (parts.length === 1) {
// 		const partLower = parts[0].toLowerCase();
// 		country = countryList.has(partLower) ? parts[0] : null;
// 		city = country ? null : parts[0];
// 	}

// 	return { ip, city, country };
// };


export const extractLocationData = (ipString) => {
	if (!ipString) {
		return { ip: null, city: null, country: null };
	}

	const parts = ipString.split('-').map((p) => p.trim());

	let ip = null;
	let city = null;
	let country = null;

	if (parts.length >= 3) {
		country = parts.pop();
		city = parts.pop();
		ip = parts.join('-'); // rejoin in case IP itself contained hyphens
	} else if (parts.length === 2) {
		[city, country] = parts;
	} else {
		ip = parts[0] || null;
	}

	return { ip, city, country };
};

/**
 * Resolve a lead's location, preferring the stored `city`/`country` fields
 * (populated on creation + backfilled via /lead/migrate-location) and falling
 * back to parsing the legacy combined `ip` ("<ip>-<city>-<country>") for leads
 * that haven't been migrated yet. Use this instead of extractLocationData(lead.ip)
 * wherever a lead's city/country is shown.
 */
export const getLeadLocation = (lead) => {
	const parsed = extractLocationData(lead?.ip);
	return {
		ip: parsed.ip,
		city: lead?.city || parsed.city,
		country: lead?.country || parsed.country,
	};
};

export const extractLocationData2 = (ipString) => {
	if (!ipString) {
		return { ip: null, city: null, country: null };
	}

	const parts = ipString.split('-').map((p) => p.trim());

	const isValidIPv4 = (str) => {
		const octets = str.split('.');
		return (
			octets.length === 4 &&
			octets.every((o) => /^\d+$/.test(o) && Number(o) >= 0 && Number(o) <= 255)
		);
	};

	let ip = null;
	let city = null;
	let country = null;

	// first part is IP
	if (isValidIPv4(parts[0])) {
		ip = parts.shift();
	}

	// last part is country
	if (parts.length > 0) {
		country = parts.pop();
	}

	// everything left is city (join back with hyphen)
	if (parts.length > 0) {
		city = parts.join('-');
	}

	return { ip, city, country };
};

export const createCountryFinder = (countries = []) => {
	const cache = new Map();

	return (value) => {
		if (!value) return null;

		value = value?.replace(/^the\s+/i, '');

		if (cache.has(value)) {
			return cache.get(value);
		}

		const country = countries.find((c) => c.name === value) || null;
		cache.set(value, country);

		return country;
	};
};

// console.log(
// 	extractLocationData('202.3.76.21-Mazar-e-sdflk Sharif-Afghanistan')
// );

export const renderValue = (value) => {
	if (!value) return 'No data';
	if (typeof value === 'string' || typeof value === 'number') return value;
	if (typeof value === 'object' && value.text) return value.text;
	return 'Invalid data';
};

export const mergeSort = (arr) => {
	if (arr.length <= 1) return arr;

	const mid = Math.floor(arr.length / 2);
	const left = mergeSort(arr.slice(0, mid));
	const right = mergeSort(arr.slice(mid));
	return merge(left, right);
};

const merge = (left, right) => {
	let sortedArr = [];
	let i = 0,
		j = 0;

	while (i < left.length && j < right.length) {
		if (
			left[i]?.fullName?.localeCompare(right[i]?.fullName) <= 0 ||
			left[i]?.firstName?.localeCompare(right[j]?.firstName) <= 0
		) {
			sortedArr.push(left[i]);
			i++;
		} else {
			sortedArr.push(right[j]);
			j++;
		}
	}

	return [...sortedArr, ...left.slice(i), ...right.slice(j)];
};

export const BRAND_COLORS = [
	'#B79045',
	'#D99A36',
	'#E5B668',
	'#EDD199',
	'#F5ECCB',
];

export const formattedValue = (value, precision = 0) =>
	Number(value).toLocaleString(undefined, {
		minimumFractionDigits: precision,
		maximumFractionDigits: precision,
	});

export const formatTime = (seconds) => {
	const safeSeconds = Math.max(0, seconds);
	const mins = Math.floor(safeSeconds / 60);
	const secs = Math.floor(safeSeconds % 60);
	return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const formatDateHeader = (date) => {
	const today = new Date();
	const yesterday = new Date(today);
	yesterday.setDate(yesterday.getDate() - 1);

	const messageDate = new Date(date);

	if (messageDate.toDateString() === today.toDateString()) {
		return 'Today';
	} else if (messageDate.toDateString() === yesterday.toDateString()) {
		return 'Yesterday';
	} else {
		return messageDate.toLocaleDateString([], {
			weekday: 'long',
			month: 'short',
			day: 'numeric',
		});
	}
};

// export const formatMessageTime = (date) => {
// 	return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
// };

export const formatMessageTime = (date) => {
	if (!date) return '';

	const tz = localStorage.getItem('timezone_cache') || currentTZ;

	return moment(date).tz(tz).format('h:mm A');
};

// Mode-aware WhatsApp palette. The VALUES are CSS vars over the `wa.*`
// semantic tokens (theme/styles.js), which carry WhatsApp Web's own light AND
// dark halves — so both chat modules flip with the color mode without any
// call-site edits. Only the brand greens stay literal: they are WhatsApp's
// identity and read on both modes.
export const whatsappColors = {
	secondary: '#00A884',
	textDark: 'var(--chakra-colors-wa-ink)',
	textLight: '#FFFFFF',
	textSecondary: 'var(--chakra-colors-wa-inkMuted)',
	sidebarBg: 'var(--chakra-colors-wa-sidebar)',

	outgoingBg: 'var(--chakra-colors-wa-outgoing)',
	incomingBg: 'var(--chakra-colors-wa-incoming)',
	primary: '#25d366',
	headerBg: 'var(--chakra-colors-wa-header)',
	inputBg: 'var(--chakra-colors-wa-input)',
	recordingDot: '#34B7F1',
	userHoverBg: 'var(--chakra-colors-wa-hover)',
	userSelectedBg: 'var(--chakra-colors-wa-selected)',
	messageHoverBg: 'var(--chakra-colors-wa-msgHover)',
	timeStampColor: 'var(--chakra-colors-wa-inkMuted)',
	replyBg: 'var(--chakra-colors-wa-reply)',
	replyBorder: 'var(--chakra-colors-wa-replyBorder)',
	chatHeaderBg: 'var(--chakra-colors-wa-header)',
	chatPanelBg: 'var(--chakra-colors-wa-panel)',

	// 2025–26 WhatsApp roles, used by the Meta inbox. Same rule: CSS vars over
	// the `wa.*` tokens, never a literal.
	app: 'var(--chakra-colors-wa-app)',
	accent: 'var(--chakra-colors-wa-accent)',
	accentHover: 'var(--chakra-colors-wa-accentHover)',
	accentInk: 'var(--chakra-colors-wa-accentInk)',
	tickRead: 'var(--chakra-colors-wa-tickRead)',
	datePill: 'var(--chakra-colors-wa-datePill)',
	divider: 'var(--chakra-colors-wa-divider)',
	doodle: 'var(--chakra-colors-wa-doodle)',
	menu: 'var(--chakra-colors-wa-menu)',
	chip: 'var(--chakra-colors-wa-chip)',
	chipActive: 'var(--chakra-colors-wa-chipActive)',
	chipActiveInk: 'var(--chakra-colors-wa-chipActiveInk)',
	composerBar: 'var(--chakra-colors-wa-composerBar)',
	recording: 'var(--chakra-colors-wa-recording)',
	overlay: 'var(--chakra-colors-wa-overlay)',
};

export const formatCallDuration = (seconds) => {
	const sec = parseInt(seconds, 10) || 0;

	const hrs = Math.floor(sec / 3600);
	const mins = Math.floor((sec % 3600) / 60);
	const remainingSecs = sec % 60;

	const parts = [];

	if (hrs > 0) parts.push(`${hrs} hr`);
	if (mins > 0 || hrs > 0) parts.push(`${mins} min`);
	parts.push(`${remainingSecs} sec`);

	return parts.join(' ');
};

export const formatName = (name) => {
	if (!name) return '';
	return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

export const removeDisableUser = (arr) => {
	return arr.filter((user) => user.isActive === true);
};
